/* FINDING A PLACE — the entry point every competitor has and this had none of.
 *
 * ═══ THERE IS NO BACKEND HERE, AND THERE DOES NOT NEED TO BE ═══
 *
 * Everything worth searching is already in the browser, loaded to draw the
 * chart: the CHS current stations, both sets of tide stations, and the
 * ActiveCaptain notes. A hosted chart that deploys as static files cannot call
 * a geocoder and should not start; an index over data already in memory
 * answers the question that actually gets asked, which is "where is Dodd
 * Narrows" and not "where is 14 Elm Street".
 *
 * ═══ AND COORDINATES ARE THE OTHER HALF ═══
 *
 * Cruising guides, the Sailing Directions and every forum post give positions
 * as degrees and decimal minutes — 49°08.3'N 123°49.1'W. Pasting one in is how
 * a plan actually starts, and until now this app had nowhere to paste it.
 * `parsePosition` takes that, plain decimal degrees, and degrees-minutes-
 * seconds, because all three are in circulation and guessing wrong puts you
 * sixty miles away.
 */

export interface LatLon {
  lat: number;
  lon: number;
}

export interface SearchItem extends LatLon {
  /** Stable within its kind. */
  id: string;
  /** What it is — "Stream", "Tide", "Note". Shown first in a result row,
   *  because whether a hit is a current station or somebody's anchorage
   *  opinion decides whether the row is worth reading. */
  kind: string;
  name: string;
  /** The one fact worth showing without opening it: "slack 09:02", "4.2/5". */
  detail?: string;
}

export interface SearchHit extends SearchItem {
  /** Higher is better. Exposed so a caller can group without re-scoring. */
  score: number;
}

/* ═══ THE SCORES ═══
 *
 * Deliberately crude, and ordered by how sure the match is rather than by
 * string distance. Fuzzy matching on a list of place names produces confident
 * nonsense — "Porlier" matching "Prevost" — and on a chart a confidently wrong
 * place is worse than no result. A prefix, a word start, then anywhere.
 */
const SCORE_EXACT = 1000;
const SCORE_PREFIX = 100;
const SCORE_WORD = 50;
const SCORE_ANYWHERE = 10;

/** Fold accents and case so "Île" matches "ile". The BC coast has enough
 *  French and Spanish names for this to matter — Malaspina, Quadra, Île. */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

function scoreName(name: string, q: string): number {
  const n = fold(name);
  if (n === q) return SCORE_EXACT;
  if (n.startsWith(q)) return SCORE_PREFIX;
  /* A word start anywhere in the name — "narrows" should find "Dodd Narrows". */
  if (new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(n)) return SCORE_WORD;
  if (n.includes(q)) return SCORE_ANYWHERE;
  return 0;
}

/**
 * Rank `items` against a query.
 *
 * Ties break on NAME LENGTH, shortest first: with "dodd" matching both "Dodd
 * Narrows" and "Dodd Narrows (south entrance)", the plain one is the one
 * somebody meant. Then alphabetically, so the order is stable between renders
 * rather than depending on which pack finished loading first.
 */
export function searchItems(
  items: readonly SearchItem[],
  query: string,
  limit = 20
): SearchHit[] {
  const q = fold(query);
  if (!q) return [];
  const hits: SearchHit[] = [];
  for (const it of items) {
    const score = scoreName(it.name, q);
    if (score > 0) hits.push({ ...it, score });
  }
  hits.sort(
    (a, b) =>
      b.score - a.score || a.name.length - b.name.length || a.name.localeCompare(b.name)
  );
  return hits.slice(0, limit);
}

/* ═══════════════════════════════════════════════════════════════════════════
   POSITIONS
   ═══════════════════════════════════════════════════════════════════════════ */

/** Degrees, minutes and seconds to decimal degrees, applying the hemisphere. */
function toDeg(d: number, m: number, s: number, hemi: string | null | undefined): number {
  const v = d + m / 60 + s / 3600;
  return hemi && /[SW]/i.test(hemi) ? -v : v;
}

/** One coordinate in any of the three notations, or null. */
function parseOne(raw: string): { value: number; hemi: string | null } | null {
  const s = raw.trim().replace(/[°º]/g, ' ').replace(/['′]/g, ' ').replace(/["″]/g, ' ');
  const m = /^([NSEW])?\s*(-?\d+(?:\.\d+)?)(?:\s+(\d+(?:\.\d+)?))?(?:\s+(\d+(?:\.\d+)?))?\s*([NSEW])?$/i.exec(
    s.trim()
  );
  if (!m) return null;
  const [, pre, dStr, mStr, sStr, post] = m;
  /* A hemisphere letter at BOTH ends is not a typo to be forgiven — "N49N" is
   * somebody's paste going wrong, and accepting it means accepting whichever
   * one happened to be read first. */
  if (pre && post) return null;
  const hemi = (pre ?? post ?? null)?.toUpperCase() ?? null;

  const d = Number(dStr);
  const min = mStr === undefined ? 0 : Number(mStr);
  const sec = sStr === undefined ? 0 : Number(sStr);
  if (!Number.isFinite(d) || min >= 60 || sec >= 60) return null;

  /* A signed value with a hemisphere letter is contradictory — "-49 N" means
   * two different things to two different tools. Rejected rather than guessed. */
  if (d < 0 && hemi) return null;

  const value = d < 0 ? -toDeg(-d, min, sec, null) : toDeg(d, min, sec, hemi);
  return { value, hemi };
}

/**
 * A pasted position, in whatever notation the guide used.
 *
 *   49°08.3'N 123°49.1'W     degrees and decimal minutes — the marine default
 *   49 08 18 N 123 49 06 W   degrees, minutes, seconds
 *   49.1383, -123.8183       decimal degrees
 *
 * ═══ WHICH NUMBER IS THE LATITUDE ═══
 *
 * From the hemisphere letters when there are any, and from the ORDER when there
 * are not — lat first, which is the convention every one of these notations
 * shares. Not from magnitude: 49 and 123 are unambiguous, but on this coast
 * "48.5, 123.3" is a real position and so is its transpose, and a rule that
 * silently swaps them is a rule that puts the boat in Washington State.
 */
export function parsePosition(input: string): LatLon | null {
  const text = input.trim();
  if (!text) return null;

  /* Split on a comma, or on the gap before a second hemisphere letter, or on
     whitespace between two coordinate groups. */
  const parts = text.includes(',')
    ? text.split(',')
    : text.split(/(?<=[NnSs])\s+|\s{2,}|\s+(?=[-\d]+[^NnSsEeWw]*[EeWw]\s*$)/);

  let a: ReturnType<typeof parseOne> = null;
  let b: ReturnType<typeof parseOne> = null;

  if (parts.length === 2) {
    a = parseOne(parts[0]!);
    b = parseOne(parts[1]!);
  } else {
    /* No usable separator: split the token run down the middle when it is an
       even number of numbers, which covers "49 08.3 N 123 49.1 W". */
    const toks = text.split(/\s+/);
    for (let i = 1; i < toks.length && !(a && b); i++) {
      const l = parseOne(toks.slice(0, i).join(' '));
      const r = parseOne(toks.slice(i).join(' '));
      if (l && r) {
        a = l;
        b = r;
      }
    }
  }
  if (!a || !b) return null;

  /* Hemispheres decide which is which when they are present; otherwise order
     does, and lat is first. */
  const aIsLat = a.hemi ? /[NS]/.test(a.hemi) : !(b.hemi && /[NS]/.test(b.hemi));
  const lat = aIsLat ? a.value : b.value;
  const lon = aIsLat ? b.value : a.value;

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

/** "49°08.3'N 123°49.1'W" — the notation the charts and the guides use, so a
 *  position copied out of this app pastes back into one of them. */
export function formatPosition(p: LatLon): string {
  const one = (v: number, pos: string, neg: string) => {
    const hemi = v < 0 ? neg : pos;
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    const m = (abs - d) * 60;
    return `${d}°${m.toFixed(1).padStart(4, '0')}'${hemi}`;
  };
  return `${one(p.lat, 'N', 'S')} ${one(p.lon, 'E', 'W')}`;
}
